import { inject, Injectable } from '@angular/core';
import { type ApiError, BizzdesignSdk } from '@bizzdesign/bizzdesign-sdk';
import { type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type ChatClient,
  type CommentEvent,
  type CommentEventFilter,
  type CommentParent,
  type CommentThread,
  type CreateCommentThreadOutput,
  type CreateCommentThreadParams,
  type DeleteCommentsOutput,
  type DeleteCommentThreadsOutput,
  type MarkCommentsOutput,
  type MarkCommentsParams,
  type ReplyToCommentThreadParams,
  type UpdateCommentOutput,
  type UpdateCommentParams,
  type UpdateCommentThreadOutput,
  type UpdateCommentThreadParams,
} from '@bizzdesign/sdk-chat';
import { type Observable } from 'rxjs';
import { type Result as TsResult } from 'ts-results';

/** Page size used when loading the threads of a frame or view. */
export const COMMENT_THREADS_PAGE_SIZE = 50;

/**
 * Upper bound on the pages loaded for one frame or view. Loading every thread
 * is forbidden for domain collections; the rest is reported through `hasMore`.
 */
export const COMMENT_THREADS_MAX_PAGES = 10;

/**
 * Threads loaded for a parent.
 *
 * @property threads - Threads newest first, as the API returned them; callers sort for display.
 * @property hasMore - `true` when older threads exist beyond the page cap.
 */
export type LoadedCommentThreads = {
  readonly threads: CommentThread[];
  readonly hasMore: boolean;
};

/** Options for {@link CommentsService.loadThreads}. */
export type LoadThreadsOptions = {
  readonly pageSize?: number;
  readonly maxPages?: number;
};

/**
 * Thin wrapper over the SDK `ChatClient` for the modeler. It unwraps the SDK
 * results so the store works with plain values and thrown `ApiError`s, and it
 * exposes the two realtime streams the store needs.
 */
@Injectable({ providedIn: 'root' })
export class CommentsService {
  private readonly sdk = inject(BizzdesignSdk);

  private get chatClient(): ChatClient {
    return this.sdk.chatClient;
  }

  /**
   * Realtime comment events for the whole tenant. The backend does not scope
   * this subscription per parent yet, so callers filter on the parent and on
   * the thread ids they know.
   *
   * @param filter - Optional event type filter.
   * @returns Stream of comment events.
   */
  events$(filter?: CommentEventFilter): Observable<CommentEvent> {
    return this.chatClient.onCommentEvents$(filter);
  }

  /**
   * Emits when the realtime WebSocket reconnects. Events sent while the socket
   * was down are lost, so the store reloads on every emission.
   */
  get connectionRestored$(): Observable<unknown> {
    return this.sdk.lowLevelClient.onConnectionRestored$;
  }

  /**
   * Loads the threads (with their comments) of a frame or view, newest first,
   * page by page, up to `maxPages`. Newest first means the cap drops the oldest
   * threads, so recent activity is never missing from the canvas.
   *
   * @param parent - Frame or view to load threads for.
   * @param options - Page size and page cap; both default to the module constants.
   * @returns The loaded threads and whether older threads were left out.
   */
  async loadThreads(
    parent: CommentParent,
    options: LoadThreadsOptions = {},
  ): Promise<LoadedCommentThreads> {
    const pageSize = options.pageSize ?? COMMENT_THREADS_PAGE_SIZE;
    const maxPages = options.maxPages ?? COMMENT_THREADS_MAX_PAGES;
    const pages = this.chatClient
      .getCommentThreads({ parent }, { field: 'createdAt', order: 'DESC' })
      .asPages({ pageSize });

    const threads: CommentThread[] = [];
    for (let index = 0; index < maxPages; index++) {
      const page = await pages.getPage(index);
      threads.push(...page);
      if (page.length < pageSize) {
        return { threads, hasMore: false };
      }
    }
    // Every page was full: only the total tells a full cap apart from more pages.
    const total = await pages.getCount();
    return { threads, hasMore: total > threads.length };
  }

  /**
   * Creates a thread with its first comment.
   *
   * @param params - Parent, text, marker coordinates and optional graphic id.
   * @returns The created comment and thread.
   */
  createThread(
    params: CreateCommentThreadParams,
  ): Promise<CreateCommentThreadOutput> {
    return unwrap(this.chatClient.createCommentThread(params));
  }

  /**
   * Adds a reply to a thread.
   *
   * @param params - Thread id and reply text.
   * @returns The created comment and its thread.
   */
  reply(
    params: ReplyToCommentThreadParams,
  ): Promise<CreateCommentThreadOutput> {
    return unwrap(this.chatClient.replyToCommentThread(params));
  }

  /**
   * Replaces the text of a comment.
   *
   * @param params - Comment id and new text.
   * @returns The updated comment and its thread.
   */
  updateComment(params: UpdateCommentParams): Promise<UpdateCommentOutput> {
    return unwrap(this.chatClient.updateComment(params));
  }

  /**
   * Deletes comments by id.
   *
   * @param ids - Comment ids.
   * @returns The deleted comments and the threads they belonged to.
   */
  deleteComments(ids: readonly UUID[]): Promise<DeleteCommentsOutput> {
    return unwrap(this.chatClient.deleteComments({ ids: [...ids] }));
  }

  /**
   * Deletes whole threads by id.
   *
   * @param ids - Thread ids.
   * @returns The deleted threads.
   */
  deleteThreads(ids: readonly UUID[]): Promise<DeleteCommentThreadsOutput> {
    return unwrap(this.chatClient.deleteCommentThreads({ ids: [...ids] }));
  }

  /**
   * Updates the marker position, graphic or root text of a thread.
   *
   * @param params - Thread id and the fields to change.
   * @returns The updated thread and its root comment.
   */
  updateThread(
    params: UpdateCommentThreadParams,
  ): Promise<UpdateCommentThreadOutput> {
    return unwrap(this.chatClient.updateCommentThread(params));
  }

  /**
   * Marks comments or whole threads as read for the current user.
   *
   * @param params - Comment ids and/or thread ids.
   * @returns The ids of the comments whose read state changed.
   */
  markAsRead(params: MarkCommentsParams): Promise<MarkCommentsOutput> {
    return unwrap(this.chatClient.markCommentsAsRead(params));
  }
}

async function unwrap<T>(result: Promise<TsResult<T, ApiError>>): Promise<T> {
  const resolved = await result;
  if (resolved.ok) {
    return resolved.val;
  }
  throw resolved.val;
}
